/// Resolves "where is this aircraft going next" — used by the
/// distance/ETA HUD beneath the score, and potentially by future
/// HUDs (e.g., a route preview line).
///
/// Priority:
///   1. directToPoint  — covers player direct-to, SID/STAR leg
///      navigation, and hold homing. The point is a live
///      pixel-space coordinate; the name is the published
///      directToName.
///   2. Goal target — for aircraft without an active directTo
///      we fall back to the goal's nominal target (airport for
///      land/arrival, waypoint for transition/takeoff/departure).
///   3. null when no useful target (already landed / completed,
///      or goal data is missing).

import type { Aircraft } from './Aircraft'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { LevelData } from '../types/LevelData'

export interface AircraftTarget {
  name: string
  /// World-pixel position (same coordinate system as
  /// `aircraft.position`).
  point: { x: number; y: number }
}

export function resolveAircraftTarget(
  aircraft: Aircraft,
  level: LevelData,
  projector: CoordinateProjector,
): AircraftTarget | null {
  // Priority 1: live directTo (player command, SID/STAR leg, hold).
  if (aircraft.directToPoint && aircraft.directToName) {
    return { name: aircraft.directToName, point: aircraft.directToPoint }
  }

  const goal = aircraft.goal

  // Priority 2: goal target.
  // Land / arrival → airport center (close enough — the runway
  // threshold is within ~1 NM of the centroid for any sensible
  // airport polygon).
  const airportName = goal.land?.airportName ?? goal.arrival?.airportName
  if (airportName) {
    const airport = level.airports.find((a) => a.name === airportName)
    if (airport) {
      return {
        name: airport.name,
        point: projector.project(airport.centerCoordinate, true),
      }
    }
  }

  // Transition / takeoff → goal waypoint.
  const wpName = goal.transition?.waypointName ?? goal.takeoff?.waypointName
  if (wpName) {
    const wp = level.waypoints.find((w) => w.name === wpName)
    if (wp) {
      return { name: wp.name, point: projector.project(wp.coordinate, true) }
    }
  }

  // Departure (SID) → SID's last leg waypoint.
  if (goal.departure) {
    const dep = goal.departure
    const airport = level.airports.find((a) => a.name === dep.airportName)
    const sid = airport?.sids?.find((s) => s.name === dep.sidName)
    const lastLeg = sid?.legs[sid.legs.length - 1]
    if (lastLeg) {
      const wp = level.waypoints.find((w) => w.name === lastLeg.waypointName)
      if (wp) {
        return { name: wp.name, point: projector.project(wp.coordinate, true) }
      }
    }
  }

  return null
}

/// Closing-speed-based ETA in seconds.
///
///   - Distance: straight-line pixel hypot ÷ pxPerNM.
///   - Closing speed: project the aircraft's velocity vector onto
///     the unit vector toward the target. If the result is
///     non-positive (heading away or stationary), ETA is null.
///
/// Heading-to-screen-vector convention matches Aircraft.tick:
///   screenAngle = (heading - 90) × π/180
///   vx = cos(screenAngle), vy = sin(screenAngle)
///   (heading 0 = N → vx=0, vy=-1 → screen-up)
export function etaSecondsTo(
  aircraft: { position: { x: number; y: number }; headingDeg: number; speedKts: number },
  target: { x: number; y: number },
  pxPerNM: number,
): { distanceNM: number; etaSec: number | null } {
  const dx = target.x - aircraft.position.x
  const dy = target.y - aircraft.position.y
  const distPx = Math.hypot(dx, dy)
  const distanceNM = distPx / pxPerNM
  if (distPx === 0) return { distanceNM: 0, etaSec: 0 }
  if (aircraft.speedKts <= 0) return { distanceNM, etaSec: null }
  const ux = dx / distPx
  const uy = dy / distPx
  const screenAngle = ((aircraft.headingDeg - 90) * Math.PI) / 180
  const vx = Math.cos(screenAngle)
  const vy = Math.sin(screenAngle)
  const closingKts = (vx * ux + vy * uy) * aircraft.speedKts
  // Threshold above zero filters out floating-point noise at the
  // perpendicular case (dot product ≈ 6e-17). A real closing speed
  // under 0.01 kt would yield an ETA in the millions of seconds —
  // effectively "not getting there", so null is the right display.
  if (closingKts < 0.01) return { distanceNM, etaSec: null }
  // distance NM ÷ closing kt × 3600 = seconds.
  const etaSec = (distanceNM / closingKts) * 3600
  return { distanceNM, etaSec }
}
